import type { createSmtP01WorkspaceAuthority } from './smt-p01-workspace.ts';
import {
  assertActiveWorkCurrentHandler,
  claimActiveWorkCustody,
  createActiveWorkCustody,
  finalizeActiveWorkCustody,
  releaseActiveWorkCustody,
  sameActiveWorkHandler,
  type ActiveWorkCustodySnapshot,
  type ActiveWorkHandlerContext,
} from './active-work-custody.ts';
import type {
  HoldSmtP01WorkspaceCommand,
  OpenSmtP01ActiveWorkspaceCommand,
  RetrieveSmtP01WorkspaceCommand,
  SmtP01ActiveWorkDependencies,
  SmtP01ActiveWorkItemViewModel,
  SmtP01ActiveWorkListViewModel,
  SmtP01PersistedWorkspaceState,
  SmtP01WorkspaceFinalizationResult,
  SmtP01WorkspaceHoldResult,
  SmtP01WorkspaceMutationContext,
  SmtP01WorkspaceMutationResult,
  SmtP01WorkspaceRetrieveResult,
  SmtP01WorkspaceViewModel,
} from './smt-p01-workspace-contract.ts';

type BaseWorkspaceAuthority = ReturnType<typeof createSmtP01WorkspaceAuthority>;
type PersistedWorkspaceCore = Omit<SmtP01PersistedWorkspaceState, 'custody'>;

const clone = <T>(value: T): T => structuredClone(value);
const errorCode = (error: unknown): string => error instanceof Error ? error.message : 'ACTIVE_WORK_REJECTED';

const assertText = (value: string | undefined, code: string): string => {
  if (!value?.trim()) throw new Error(code);
  return value.trim();
};

const handlerFromMutation = (context: SmtP01WorkspaceMutationContext): ActiveWorkHandlerContext => {
  if (context.surface !== 'smt' && context.surface !== 'smm') throw new Error('ACTIVE_WORK_SURFACE_REQUIRED');
  return {
    actorId: assertText(context.actorId, 'ACTIVE_WORK_ACTOR_REQUIRED'),
    surface: context.surface,
    deviceId: assertText(context.deviceId, 'ACTIVE_WORK_DEVICE_REQUIRED'),
    at: context.at,
    ...(context.sessionRef?.trim() ? { sessionRef: context.sessionRef.trim() } : {}),
  };
};

const handlerFromRetrieve = (command: RetrieveSmtP01WorkspaceCommand): ActiveWorkHandlerContext => ({
  actorId: assertText(command.actorId, 'ACTIVE_WORK_ACTOR_REQUIRED'),
  surface: command.surface,
  deviceId: assertText(command.deviceId, 'ACTIVE_WORK_DEVICE_REQUIRED'),
  at: command.at,
  ...(command.sessionRef?.trim() ? { sessionRef: command.sessionRef.trim() } : {}),
});

export function createSmtP01ActiveWorkAuthority(
  base: BaseWorkspaceAuthority,
  activeWork?: SmtP01ActiveWorkDependencies,
) {
  const custodyByWorkspace = new Map<string, ActiveWorkCustodySnapshot>();

  const support = (): SmtP01ActiveWorkDependencies => {
    if (!activeWork) throw new Error('ACTIVE_WORK_RUNTIME_UNAVAILABLE');
    return activeWork;
  };

  const contextNow = () => {
    const deps = support();
    const storeId = assertText(deps.getStoreId() ?? undefined, 'ACTIVE_WORK_STORE_ID_UNAVAILABLE');
    const businessDate = assertText(deps.getBusinessDate() ?? undefined, 'ACTIVE_WORK_BUSINESS_DATE_UNAVAILABLE');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(businessDate)) throw new Error('ACTIVE_WORK_BUSINESS_DATE_INVALID');
    return { deps, storeId, businessDate };
  };

  const assertActiveStaff = (actorId: string): void => {
    const deps = support();
    if (!deps.isActiveStaff(actorId)) throw new Error('ACTIVE_WORK_STAFF_NOT_ACTIVE');
  };

  const withCustody = (
    snapshot: SmtP01WorkspaceViewModel,
    custody?: ActiveWorkCustodySnapshot,
  ): SmtP01WorkspaceViewModel => custody ? clone({ ...snapshot, custody }) : clone(snapshot);

  const workspaceItem = (
    snapshot: SmtP01WorkspaceViewModel,
    custody: ActiveWorkCustodySnapshot,
  ): SmtP01ActiveWorkItemViewModel => {
    const held = [...custody.history].reverse().find((event) => event.type === 'released');
    return clone({
      sourceKind: 'p01_workspace',
      workspaceId: snapshot.workspaceId,
      workspaceRevision: snapshot.revision,
      custodyRevision: custody.custodyRevision,
      status: custody.status === 'available' ? 'available' : 'claimed',
      serviceMode: snapshot.serviceMode,
      createdBy: custody.createdBy,
      currentHandler: custody.currentHandler,
      lastHandler: custody.lastHandler,
      heldAt: held?.handler.at ?? null,
      itemCount: snapshot.lines.reduce((sum, line) => sum + line.quantity, 0),
      pendingRequiredTaskCount: snapshot.pendingRequiredTaskCount,
      pricing: snapshot.pricing,
    });
  };

  const persistedItem = (record: SmtP01PersistedWorkspaceState): SmtP01ActiveWorkItemViewModel =>
    workspaceItem(base.projectPersistenceState(record), record.custody);

  const loadCheckpoint = async () => {
    const { deps, storeId, businessDate } = contextNow();
    const checkpoint = await deps.durability.load(storeId);
    if (checkpoint && checkpoint.businessDate !== businessDate && checkpoint.items.length > 0) {
      throw new Error('ACTIVE_WORK_BUSINESS_DATE_MISMATCH');
    }
    return { deps, storeId, businessDate, checkpoint };
  };

  const fact = (
    type: 'active_work.opened' | 'active_work.held' | 'active_work.retrieved' | 'active_work.finalized',
    workspaceId: string,
    handler: ActiveWorkHandlerContext,
    custody: ActiveWorkCustodySnapshot,
    workspaceRevision: number,
    orderId?: string,
  ): void => {
    const { deps, businessDate } = contextNow();
    deps.appendFact?.({
      factType: type,
      workspaceId,
      businessDate,
      handler,
      custodyRevision: custody.custodyRevision,
      workspaceRevision,
      ...(orderId ? { orderId } : {}),
    });
  };

  const guardMutation = (context: SmtP01WorkspaceMutationContext): void => {
    const custody = custodyByWorkspace.get(context.workspaceId);
    if (!custody) return;
    assertActiveStaff(context.actorId);
    assertActiveWorkCurrentHandler(custody, handlerFromMutation(context));
  };

  const attachMutationCustody = <T>(
    workspaceId: string,
    result: SmtP01WorkspaceMutationResult<T>,
  ): SmtP01WorkspaceMutationResult<T> => {
    const custody = custodyByWorkspace.get(workspaceId);
    if (!custody) return result;
    return clone({ ...result, snapshot: withCustody(result.snapshot, custody) });
  };

  const mutate = <TCommand, TValue>(
    method: (context: SmtP01WorkspaceMutationContext, command: TCommand) => SmtP01WorkspaceMutationResult<TValue>,
    context: SmtP01WorkspaceMutationContext,
    command: TCommand,
  ): SmtP01WorkspaceMutationResult<TValue> => {
    const custody = custodyByWorkspace.get(context.workspaceId);
    if (custody) {
      const snapshot = base.snapshot(context.workspaceId);
      if (context.expectedRevision !== snapshot.revision) {
        return { kind: 'rejected', code: 'SMT_WORKSPACE_REVISION_CONFLICT', snapshot: withCustody(snapshot, custody) };
      }
      try {
        guardMutation(context);
      } catch (error) {
        return { kind: 'rejected', code: errorCode(error), snapshot: withCustody(snapshot, custody) };
      }
    }
    return attachMutationCustody(context.workspaceId, method(context, command));
  };

  const rejectHold = (code: string, workspaceId: string): SmtP01WorkspaceHoldResult => {
    if (base.hasWorkspace(workspaceId)) {
      const custody = custodyByWorkspace.get(workspaceId);
      if (custody) return { kind: 'rejected', code, item: workspaceItem(base.snapshot(workspaceId), custody) };
    }
    return { kind: 'rejected', code };
  };

  const rejectRetrieve = (
    code: string,
    item?: SmtP01ActiveWorkItemViewModel,
  ): SmtP01WorkspaceRetrieveResult => item ? { kind: 'rejected', code, item } : { kind: 'rejected', code };

  return {
    openWorkspace: base.openWorkspace,

    openActiveWorkspace(command: OpenSmtP01ActiveWorkspaceCommand): SmtP01WorkspaceViewModel {
      support();
      assertActiveStaff(command.handler.actorId);
      const existingCustody = custodyByWorkspace.get(command.workspaceId);
      if (base.hasWorkspace(command.workspaceId)) {
        if (!existingCustody) throw new Error('ACTIVE_WORK_WORKSPACE_ID_CONFLICT');
        if (!sameActiveWorkHandler(existingCustody.currentHandler, command.handler)) throw new Error('ACTIVE_WORK_ALREADY_CLAIMED');
        return withCustody(base.snapshot(command.workspaceId), existingCustody);
      }
      const snapshot = base.openWorkspace({
        workspaceId: command.workspaceId,
        actorId: command.handler.actorId,
        at: command.handler.at,
        ...(command.initialMode ? { initialMode: command.initialMode } : {}),
        ...(command.initialServiceMode ? { initialServiceMode: command.initialServiceMode } : {}),
      });
      const custody = createActiveWorkCustody(command.handler, snapshot.revision);
      custodyByWorkspace.set(snapshot.workspaceId, custody);
      fact('active_work.opened', snapshot.workspaceId, command.handler, custody, snapshot.revision);
      return withCustody(snapshot, custody);
    },

    snapshot(workspaceId: string): SmtP01WorkspaceViewModel {
      const snapshot = base.snapshot(workspaceId);
      return withCustody(snapshot, custodyByWorkspace.get(workspaceId));
    },

    comboProposals: base.comboProposals,

    prepareFinalization(workspaceId: string, expectedRevision: number): SmtP01WorkspaceFinalizationResult {
      const custody = custodyByWorkspace.get(workspaceId);
      if (!custody) return base.prepareFinalization(workspaceId, expectedRevision);
      return {
        kind: 'rejected',
        code: 'ACTIVE_WORK_HANDLER_CONTEXT_REQUIRED',
        snapshot: withCustody(base.snapshot(workspaceId), custody),
      };
    },

    prepareActiveFinalization(
      workspaceId: string,
      expectedRevision: number,
      handler: ActiveWorkHandlerContext,
      expectedCustodyRevision: number,
    ): SmtP01WorkspaceFinalizationResult {
      const custody = custodyByWorkspace.get(workspaceId);
      if (!custody) return {
        kind: 'rejected', code: 'ACTIVE_WORK_CUSTODY_REQUIRED', snapshot: base.snapshot(workspaceId),
      };
      const snapshot = base.snapshot(workspaceId);
      if (snapshot.revision !== expectedRevision) return {
        kind: 'rejected', code: 'SMT_WORKSPACE_REVISION_CONFLICT', snapshot: withCustody(snapshot, custody),
      };
      if (custody.custodyRevision !== expectedCustodyRevision) return {
        kind: 'rejected', code: 'ACTIVE_WORK_CUSTODY_REVISION_CONFLICT', snapshot: withCustody(snapshot, custody),
      };
      try {
        assertActiveStaff(handler.actorId);
        assertActiveWorkCurrentHandler(custody, handler);
      } catch (error) {
        return { kind: 'rejected', code: errorCode(error), snapshot: withCustody(snapshot, custody) };
      }
      const prepared = base.prepareFinalization(workspaceId, expectedRevision);
      return prepared.kind === 'rejected'
        ? { ...prepared, snapshot: withCustody(prepared.snapshot, custody) }
        : prepared;
    },

    async completeActiveFinalization(input: {
      readonly workspaceId: string;
      readonly expectedWorkspaceRevision: number;
      readonly expectedCustodyRevision: number;
      readonly handler: ActiveWorkHandlerContext;
      readonly orderId: string;
    }): Promise<ActiveWorkCustodySnapshot> {
      const custody = custodyByWorkspace.get(input.workspaceId);
      if (!custody) throw new Error('ACTIVE_WORK_CUSTODY_REQUIRED');
      const snapshot = base.snapshot(input.workspaceId);
      if (snapshot.revision !== input.expectedWorkspaceRevision) throw new Error('SMT_WORKSPACE_REVISION_CONFLICT');
      assertActiveStaff(input.handler.actorId);
      const finalized = finalizeActiveWorkCustody(
        custody, input.handler, input.expectedCustodyRevision, input.expectedWorkspaceRevision,
      );
      const { deps, storeId, businessDate, checkpoint } = await loadCheckpoint();
      const persisted = checkpoint?.items ?? [];
      if (persisted.some((record) => record.workspaceId === input.workspaceId)) {
        await deps.durability.save({
          storeId,
          businessDate,
          items: persisted.filter((record) => record.workspaceId !== input.workspaceId),
          expectedRevision: checkpoint?.revision ?? 0,
          committedAt: input.handler.at,
        });
      }
      fact('active_work.finalized', input.workspaceId, input.handler, finalized, input.expectedWorkspaceRevision, input.orderId);
      custodyByWorkspace.delete(input.workspaceId);
      base.removeWorkspace(input.workspaceId);
      return clone(finalized);
    },

    setMode: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.setMode>[1]) =>
      mutate(base.setMode, context, command),
    setServiceMode: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.setServiceMode>[1]) =>
      mutate(base.setServiceMode, context, command),
    setLineServiceMode: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.setLineServiceMode>[1]) =>
      mutate(base.setLineServiceMode, context, command),
    addProduct: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.addProduct>[1]) =>
      mutate(base.addProduct, context, command),
    updateLine: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.updateLine>[1]) =>
      mutate(base.updateLine, context, command),
    removeLine: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.removeLine>[1]) =>
      mutate(base.removeLine, context, command),
    setKeepSingle: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.setKeepSingle>[1]) =>
      mutate(base.setKeepSingle, context, command),
    createComboGroup: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.createComboGroup>[1]) =>
      mutate(base.createComboGroup, context, command),
    assignComboSlot: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.assignComboSlot>[1]) =>
      mutate(base.assignComboSlot, context, command),
    unassignComboSlot: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.unassignComboSlot>[1]) =>
      mutate(base.unassignComboSlot, context, command),
    confirmComboProposal: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.confirmComboProposal>[1]) =>
      mutate(base.confirmComboProposal, context, command),
    assignQuickDrink: (context: SmtP01WorkspaceMutationContext, command: Parameters<typeof base.assignQuickDrink>[1]) =>
      mutate(base.assignQuickDrink, context, command),

    async holdWorkspace(command: HoldSmtP01WorkspaceCommand): Promise<SmtP01WorkspaceHoldResult> {
      if (!base.hasWorkspace(command.workspaceId)) {
        const { checkpoint } = await loadCheckpoint();
        const record = checkpoint?.items.find((item) => item.workspaceId === command.workspaceId);
        if (!record) return { kind: 'rejected', code: 'ACTIVE_WORK_NOT_FOUND' };
        const lastRelease = [...record.custody.history].reverse().find((event) => event.type === 'released');
        let handler: ActiveWorkHandlerContext;
        try {
          handler = handlerFromMutation(command);
        } catch (error) {
          return { kind: 'rejected', code: errorCode(error), item: persistedItem(record) };
        }
        if (
          record.custody.status === 'available'
          && record.revision === command.expectedRevision
          && record.custody.custodyRevision === command.expectedCustodyRevision + 1
          && lastRelease?.workRevision === command.expectedRevision
          && sameActiveWorkHandler(record.custody.lastHandler, handler)
        ) {
          return { kind: 'accepted', item: persistedItem(record) };
        }
        return { kind: 'rejected', code: 'ACTIVE_WORK_NOT_FOUND', item: persistedItem(record) };
      }

      const custody = custodyByWorkspace.get(command.workspaceId);
      if (!custody) return rejectHold('ACTIVE_WORK_CUSTODY_REQUIRED', command.workspaceId);
      const snapshot = base.snapshot(command.workspaceId);
      if (snapshot.revision !== command.expectedRevision) return rejectHold('SMT_WORKSPACE_REVISION_CONFLICT', command.workspaceId);
      if (custody.custodyRevision !== command.expectedCustodyRevision) return rejectHold('ACTIVE_WORK_CUSTODY_REVISION_CONFLICT', command.workspaceId);

      let released: ActiveWorkCustodySnapshot;
      let handler: ActiveWorkHandlerContext;
      try {
        handler = handlerFromMutation(command);
        assertActiveStaff(command.actorId);
        released = releaseActiveWorkCustody(custody, handler, command.expectedCustodyRevision, snapshot.revision);
      } catch (error) {
        return rejectHold(errorCode(error), command.workspaceId);
      }

      const core = base.exportPersistenceState(command.workspaceId) as PersistedWorkspaceCore;
      const record: SmtP01PersistedWorkspaceState = clone({ ...core, custody: released });
      const { deps, storeId, businessDate, checkpoint } = await loadCheckpoint();
      const currentItems = checkpoint?.items ?? [];
      await deps.durability.save({
        storeId,
        businessDate,
        items: [...currentItems.filter((item) => item.workspaceId !== command.workspaceId), record],
        expectedRevision: checkpoint?.revision ?? 0,
        committedAt: command.at,
      });

      const item = workspaceItem(snapshot, released);
      fact('active_work.held', command.workspaceId, handler, released, snapshot.revision);
      custodyByWorkspace.delete(command.workspaceId);
      base.removeWorkspace(command.workspaceId);
      return { kind: 'accepted', item };
    },

    async retrieveWorkspace(command: RetrieveSmtP01WorkspaceCommand): Promise<SmtP01WorkspaceRetrieveResult> {
      support();
      let handler: ActiveWorkHandlerContext;
      try {
        assertActiveStaff(command.actorId);
        handler = handlerFromRetrieve(command);
      } catch (error) {
        return rejectRetrieve(errorCode(error));
      }

      if (base.hasWorkspace(command.workspaceId)) {
        const custody = custodyByWorkspace.get(command.workspaceId);
        if (!custody) return rejectRetrieve('ACTIVE_WORK_WORKSPACE_ID_CONFLICT');
        const snapshot = base.snapshot(command.workspaceId);
        if (
          sameActiveWorkHandler(custody.currentHandler, handler)
          && (
            (snapshot.revision === command.expectedWorkspaceRevision && custody.custodyRevision === command.expectedCustodyRevision)
            || (snapshot.revision === command.expectedWorkspaceRevision + 1 && custody.custodyRevision === command.expectedCustodyRevision + 1)
          )
        ) {
          return { kind: 'accepted', snapshot: withCustody(snapshot, custody), custody: clone(custody) };
        }
        if (snapshot.revision !== command.expectedWorkspaceRevision) {
          return rejectRetrieve('SMT_WORKSPACE_REVISION_CONFLICT', workspaceItem(snapshot, custody));
        }
        if (custody.custodyRevision !== command.expectedCustodyRevision) {
          return rejectRetrieve('ACTIVE_WORK_CUSTODY_REVISION_CONFLICT', workspaceItem(snapshot, custody));
        }
        return rejectRetrieve('ACTIVE_WORK_ALREADY_CLAIMED', workspaceItem(snapshot, custody));
      }

      const { deps, storeId, businessDate, checkpoint } = await loadCheckpoint();
      const record = checkpoint?.items.find((item) => item.workspaceId === command.workspaceId);
      if (!record) return rejectRetrieve('ACTIVE_WORK_NOT_FOUND');
      const currentItem = persistedItem(record);
      if (record.revision !== command.expectedWorkspaceRevision) return rejectRetrieve('SMT_WORKSPACE_REVISION_CONFLICT', currentItem);
      if (record.custody.custodyRevision !== command.expectedCustodyRevision) {
        return rejectRetrieve('ACTIVE_WORK_CUSTODY_REVISION_CONFLICT', currentItem);
      }

      if (record.custody.status === 'claimed') {
        if (!sameActiveWorkHandler(record.custody.currentHandler, handler)) {
          return rejectRetrieve('ACTIVE_WORK_ALREADY_CLAIMED', currentItem);
        }
        const core: PersistedWorkspaceCore = clone({
          workspaceId: record.workspaceId,
          actorId: command.actorId,
          revision: record.revision,
          mode: record.mode,
          serviceMode: record.serviceMode,
          lineServiceModes: record.lineServiceModes,
          lines: record.lines,
          comboGroups: record.comboGroups,
          keepSingleUnitKeys: record.keepSingleUnitKeys,
        });
        base.projectPersistenceState(core);
        const snapshot = base.restorePersistenceState(core);
        custodyByWorkspace.set(command.workspaceId, clone(record.custody));
        return { kind: 'accepted', snapshot: withCustody(snapshot, record.custody), custody: clone(record.custody) };
      }

      if (record.custody.status !== 'available') return rejectRetrieve('ACTIVE_WORK_NOT_CLAIMABLE', currentItem);

      let claimed: ActiveWorkCustodySnapshot;
      try {
        claimed = claimActiveWorkCustody(
          record.custody,
          handler,
          command.expectedCustodyRevision,
          record.revision + 1,
        );
      } catch (error) {
        return rejectRetrieve(errorCode(error), currentItem);
      }

      const core: PersistedWorkspaceCore = clone({
        workspaceId: record.workspaceId,
        actorId: command.actorId,
        revision: record.revision + 1,
        mode: record.mode,
        serviceMode: record.serviceMode,
        lineServiceModes: record.lineServiceModes,
        lines: record.lines,
        comboGroups: record.comboGroups,
        keepSingleUnitKeys: record.keepSingleUnitKeys,
      });
      base.projectPersistenceState(core);
      const nextRecord: SmtP01PersistedWorkspaceState = clone({ ...core, custody: claimed });

      try {
        await deps.durability.save({
          storeId,
          businessDate,
          items: (checkpoint?.items ?? []).map((item) => item.workspaceId === command.workspaceId ? nextRecord : item),
          expectedRevision: checkpoint?.revision ?? 0,
          committedAt: command.at,
        });
      } catch (error) {
        if (!errorCode(error).startsWith('DURABILITY_REVISION_CONFLICT')) throw error;
        const latest = await deps.durability.load(storeId);
        const latestRecord = latest?.items.find((item) => item.workspaceId === command.workspaceId);
        if (latestRecord) {
          return rejectRetrieve(
            latestRecord.custody.status === 'claimed' ? 'ACTIVE_WORK_ALREADY_CLAIMED' : 'ACTIVE_WORK_CUSTODY_REVISION_CONFLICT',
            persistedItem(latestRecord),
          );
        }
        return rejectRetrieve('ACTIVE_WORK_CUSTODY_REVISION_CONFLICT');
      }

      const snapshot = base.restorePersistenceState(core);
      custodyByWorkspace.set(command.workspaceId, claimed);
      fact('active_work.retrieved', command.workspaceId, handler, claimed, snapshot.revision);
      return { kind: 'accepted', snapshot: withCustody(snapshot, claimed), custody: clone(claimed) };
    },

    async activeWork(): Promise<SmtP01ActiveWorkListViewModel> {
      const { checkpoint } = await loadCheckpoint();
      const byId = new Map<string, SmtP01ActiveWorkItemViewModel>();
      for (const record of checkpoint?.items ?? []) byId.set(record.workspaceId, persistedItem(record));
      for (const [workspaceId, custody] of custodyByWorkspace) {
        if (!base.hasWorkspace(workspaceId)) continue;
        byId.set(workspaceId, workspaceItem(base.snapshot(workspaceId), custody));
      }
      return {
        items: [...byId.values()]
          .sort((left, right) => left.workspaceId.localeCompare(right.workspaceId))
          .map(clone),
      };
    },
  };
}
